import React from 'react';
import { Button, Col, FormGroup, FormLabel, Modal, ModalProps, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik } from 'formik';
import { object, string, date } from 'yup';

import { CorporateUser, Genders, tGender } from '@models/User';
import { FormikControl, FormikSelect } from '@components/Base';
import { UpdateUserCorporateHealthQuoteParams } from '@apis/CorporateHealthQuoteApi';

export type UserEditFormValues = Omit<UpdateUserCorporateHealthQuoteParams, 'orderNumber' | 'userId'>;

const EditForm = ({ onHide }: Pick<ModalProps, 'onHide'>) => {
  const { t } = useTranslation();
  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>{t('common:edit')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup as={Row} className="mb-3" controlId="name">
          <FormLabel column sm={4}>
            {t('user.name')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="name" />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="dob">
          <FormLabel column sm={4}>
            {t('user.dob')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="dob" type="date" />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="gender">
          <FormLabel column sm={4}>
            {t('user.gender')}*
          </FormLabel>
          <Col sm>
            <FormikSelect name="gender">
              <option value={Genders.Female}>{tGender(Genders.Female)}</option>
              <option value={Genders.Male}>{tGender(Genders.Male)}</option>
            </FormikSelect>
          </Col>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:update')}
        </Button>
      </Modal.Footer>
    </Form>
  );
}

export const useValidationSchema = () => {
  const { t } = useTranslation();

  return object({
    name: string().required(t('validation:required')),
    dob: date().required(t('validation:required')),
    gender: string().required(t('validation:required')),
  });
};

type Props = ModalProps & {
  user: CorporateUser | null;
  onSubmit: (values: UserEditFormValues) => void;
}

const UserEditFormModal = ({ user, onSubmit, ...props }: Props) => {
  const validationSchema = useValidationSchema();

  if (!user) return null;

  const initialValues: UserEditFormValues = {
    name: user.name,
    dob: user.dob,
    gender: user.gender,
  }

  return (
    <Modal {...props}>
      {user && (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          <EditForm onHide={props.onHide} />
        </Formik>
      )}
    </Modal>
  )
};

export { UserEditFormModal };
